import { formatProductCategoryPath, type ProductPrimaryId } from "@/data/product-categories";
import type { DoOrDumpTimeToFirstSignal } from "@/data/do-or-dump-funnel-fields";
import { computeDoOrDumpDerived, sortAcquisitions, type DoOrDumpAcquisition } from "@/lib/do-or-dump-compute";

/** Validated funnel state — same shape used by Express POST and static client insert. */
export type ValidatedDoOrDumpFunnelInsert = {
  email: string;
  productPrimary: ProductPrimaryId;
  productSub: string;
  productNotes: string;
  hoursPerWeek: number;
  followers: number;
  avgViews: number;
  acquisitions: DoOrDumpAcquisition[];
  price: number;
  cost: number;
  ordersPerMonth: number;
  goalMonthly: number;
  goalCustomMode: boolean;
  investmentBudgetUsd: number;
  isCreator: boolean;
  creatorIgFollowers: number | null;
  creatorTiktokFollowers: number | null;
  creatorYoutubeSubscribers: number | null;
  timeToFirstSignal: DoOrDumpTimeToFirstSignal;
  whyYou: string;
  productDifferentiation: string;
};

/** Row for `public.do_or_dump_funnel_events` insert (snake_case). */
export function buildDoOrDumpFunnelInsertRow(v: ValidatedDoOrDumpFunnelInsert): Record<string, unknown> {
  const acquisitions = sortAcquisitions(v.acquisitions);
  const acquisition = acquisitions.join(",");
  const derived = computeDoOrDumpDerived({
    price: v.price,
    cost: v.cost,
    ordersPerMonth: v.ordersPerMonth,
    goalMonthly: v.goalMonthly,
    hoursPerWeek: v.hoursPerWeek,
    followers: v.followers,
    acquisitions,
  });
  const categoryPath = formatProductCategoryPath(v.productPrimary, v.productSub, v.productNotes);
  return {
    email: v.email.trim().toLowerCase(),
    product_primary: v.productPrimary,
    product_sub: v.productSub,
    product_notes: v.productNotes,
    category_path: categoryPath,
    hours_per_week: v.hoursPerWeek,
    followers: Math.round(v.followers),
    avg_views: Math.round(v.avgViews),
    acquisition,
    price: v.price,
    cost: v.cost,
    orders_per_month: Math.round(v.ordersPerMonth),
    goal_monthly: v.goalMonthly,
    goal_custom_mode: v.goalCustomMode,
    investment_budget_usd: v.investmentBudgetUsd,
    profit_per_item: derived.profitPerItem,
    monthly_profit_guess: derived.monthlyProfitGuess,
    momentum: derived.momentum,
    capacity: derived.capacity,
    units_for_2k: derived.unitsFor2k,
    units_for_goal: derived.unitsForGoal,
    verdict: derived.verdict,
    verdict_line: derived.verdictLine,
    time_tier: derived.timeTier,
    audience_tier: derived.audienceTier,
    acquisition_tier: derived.acquisitionTier,
    is_creator: v.isCreator,
    creator_ig_followers: v.creatorIgFollowers,
    creator_tiktok_followers: v.creatorTiktokFollowers,
    creator_youtube_subscribers: v.creatorYoutubeSubscribers,
    time_to_first_signal: v.timeToFirstSignal,
    why_you: v.whyYou.trim(),
    product_differentiation: v.productDifferentiation.trim(),
    marketing_emails_consent: true,
  };
}
